import { Line, Text } from "@react-three/drei";
import { floorplannerStore } from "../../store/floorplannerStore";
import { editorStore } from "../../store/editorStore";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import { observer } from "mobx-react-lite";
import { isRulerLineType, isSingleLineType, isWallType, RulerLineType, SingleLineType, WallType, WallConnectionStart, WallConnectionEnd } from "../../types/floorplannerTypes";
import { calculateObjectPositionAndRotation } from "./calculateObjectPositionAndRotation";
import { displayLengthToWorld, formatLength, worldToDisplayLength } from "../../utils/conversions";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SimpleLineSegments } from "./SimpleLineSegments";
import { renderStore } from "../../store/renderStore";
import { SimpleText } from "../Text/SimpleText";
import { calculateResolvedEdgeConnectionMeasurementSpan, calculateWallMeasurementSpan } from "../../utils/measurementLine";

interface MeasurementLineProps {
  object: WallType | SingleLineType | RulerLineType;
}

const MeasurementLineComponent: React.FC<MeasurementLineProps> = observer(({ object }) => {
  const [tempLength, setTempLength] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const disablePointerEvents = editorStore.wallConstructionMode;

  // All hooks must be called before any early returns
  const innerPerpendicular = floorplannerStore.objectPerpendicularInner(object);
  const outerPerpendicular = floorplannerStore.objectPerpendicularOuter(object);

  const rulerLineOffset =
    0.2 + (isWallType(object) ? (object.wallWidth || floorplannerStore.wallWidth) / 2 : 0);
  const rulerOuterTextOffset =
    0.2 + (isWallType(object) ? (object.wallWidth || floorplannerStore.wallWidth) / 2 : 0);
  const rulerInnerTextOffset =
    0.13 + (isWallType(object) ? (object.wallWidth || floorplannerStore.wallWidth) / 2 : 0);

  const { position, rotation, textRotation } = calculateObjectPositionAndRotation(object.start, object.end);

  // Check if measurements should be shown based on zoom level
  // Do this after all hooks are called but before expensive computations
  const shouldShowMeasurements = editorStore.shouldShowMeasurements();

  // Close input if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        if (editorStore.wallEditingOuterLength === object.id) {
          editorStore.setWallEditingOuterLength(undefined);
        }
        if (editorStore.wallEditingInnerLength === object.id) {
          editorStore.setWallEditingInnerLength(undefined);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [object.id]);

  const measurementScale = editorStore.getMeasurementVisibilityScale();

  const measurementZoomLevelDivisor = editorStore.measurementZoomLevelDivisor();
  const rulerTextBoxPadding = (0.13 / measurementZoomLevelDivisor) * measurementScale;
  const rulerTextBoxHeight = (0.16 / measurementZoomLevelDivisor) * measurementScale;
  const rulerTextBoxWidth = (0.3 / measurementZoomLevelDivisor) * measurementScale;
  const rulerTextFontSize = (14 / measurementZoomLevelDivisor) * measurementScale;
  const stopLineLength = 0.1;

  // Compute all line segment points - always call useMemo
  const {
    segmentPoints: segments,
    measurementLineStart,
    measurementLineEnd,
    offsetPerpendicular,
    chosenSide,
    measurementLength,
    hiddenMeasurementLength,
  } = useMemo(() => {
    const wallDirectionVec = new THREE.Vector2().subVectors(object.end, object.start);
    const wallLength = wallDirectionVec.length();
    const wallDirectionNormalized = wallDirectionVec.clone().normalize();

    const leftPerpendicular = new THREE.Vector2(-wallDirectionNormalized.y, wallDirectionNormalized.x);
    const rightPerpendicular = leftPerpendicular.clone().negate();

    let offsetPerp = outerPerpendicular.clone();
    let chosenSide: "left" | "right" | null = null;
    let measurementStartT = 0;
    let measurementEndT = wallLength;

    if (isWallType(object) && wallLength > 1e-6) {
      const leftExtent = floorplannerStore.getOutlineSideExtents(object, "left");
      const rightExtent = floorplannerStore.getOutlineSideExtents(object, "right");
      chosenSide = floorplannerStore.getMeasurementDisplaySide(object, leftExtent, rightExtent);
      offsetPerp = chosenSide === "left" ? leftPerpendicular : rightPerpendicular;

      if (object.lineForm === "arc" && object.controlPoint) {
        // Curved walls should keep using the visible inner span, not the raw
        // chord length. The room-height label on a curved side is expected to
        // match the straight inner opening, which the outline extents capture.
        const innerSide = floorplannerStore.getInnerMeasurementSide(object);
        const innerExtent = innerSide === "left"
          ? leftExtent
          : innerSide === "right"
            ? rightExtent
            : null;

        const measurementSpan = innerExtent
          ? calculateWallMeasurementSpan(wallLength, innerExtent, null)
          : calculateWallMeasurementSpan(wallLength, leftExtent, rightExtent);
        measurementStartT = measurementSpan.startT;
        measurementEndT = measurementSpan.endT;
      } else {
        const resolvedEdgeSpan = calculateResolvedEdgeConnectionMeasurementSpan(object, wallLength);
        if (resolvedEdgeSpan) {
          measurementStartT = resolvedEdgeSpan.startT;
          measurementEndT = resolvedEdgeSpan.endT;
        } else {
          // Straight walls without resolved edge joins keep using the reference
          // line. Outline trimming can shave half a wall width from one end and
          // under-report standalone dimensions.
          measurementStartT = 0;
          measurementEndT = wallLength;
        }
      }
    }

    const segmentPoints: [number, number, number][] = [];

    let measurementStartPoint = object.start.clone().add(
      wallDirectionNormalized.clone().multiplyScalar(measurementStartT)
    );
    let measurementEndPoint = object.start.clone().add(
      wallDirectionNormalized.clone().multiplyScalar(measurementEndT)
    );

    let measurementLineStartVec = new THREE.Vector3(
      measurementStartPoint.x + offsetPerp.x * rulerLineOffset,
      measurementStartPoint.y + offsetPerp.y * rulerLineOffset,
      0
    );
    let measurementLineEndVec = new THREE.Vector3(
      measurementEndPoint.x + offsetPerp.x * rulerLineOffset,
      measurementEndPoint.y + offsetPerp.y * rulerLineOffset,
      0
    );

    if (isWallType(object) && object.lineForm === "arc" && object.controlPoint) {
      // For curved walls, we use the actual outline corners so the length
      // matches a manual straight-line measurement exactly.
      const outlineSegments = renderStore.getOutlineSegments(object.id);
      
      const getCornersForSide = (segments: any[]) => {
        if (!segments || segments.length === 0) return null;
        let startCorner = segments[0].start.clone();
        let endCorner = segments[0].start.clone();
        let minDistStart = Infinity;
        let minDistEnd = Infinity;
        for (const seg of segments) {
          for (const pt of [seg.start, seg.end]) {
            const dStart = pt.distanceToSquared(object.start);
            if (dStart < minDistStart) { minDistStart = dStart; startCorner = pt.clone(); }
            const dEnd = pt.distanceToSquared(object.end);
            if (dEnd < minDistEnd) { minDistEnd = dEnd; endCorner = pt.clone(); }
          }
        }
        return { startCorner, endCorner, length: startCorner.distanceTo(endCorner) };
      };

      const leftCorners = getCornersForSide(outlineSegments?.leftSegments || []);
      const rightCorners = getCornersForSide(outlineSegments?.rightSegments || []);

      const innerSide = floorplannerStore.getInnerMeasurementSide(object);
      let bestCorners = null;
      if (innerSide === "left") bestCorners = leftCorners;
      else if (innerSide === "right") bestCorners = rightCorners;
      else {
        if (leftCorners && rightCorners) {
          bestCorners = leftCorners.length < rightCorners.length ? leftCorners : rightCorners;
        } else {
          bestCorners = leftCorners || rightCorners;
        }
      }

      if (bestCorners) {
        measurementStartPoint = bestCorners.startCorner;
        measurementEndPoint = bestCorners.endCorner;
        
        // To keep the line parallel to the chord between the two points:
        const chordDir = measurementEndPoint.clone().sub(measurementStartPoint).normalize();
        const chordPerp = new THREE.Vector3(-chordDir.y, chordDir.x, 0);
        // Ensure it points outward
        if (chordPerp.clone().dot(new THREE.Vector3(offsetPerp.x, offsetPerp.y, 0)) < 0) {
          chordPerp.negate();
        }
        
        // Since measurementStartPoint is already at the wall edge, we just add the outer text offset (0.2)
        const extraOffset = 0.2;
        measurementLineStartVec = new THREE.Vector3(measurementStartPoint.x, measurementStartPoint.y, 0).add(chordPerp.clone().multiplyScalar(extraOffset));
        measurementLineEndVec = new THREE.Vector3(measurementEndPoint.x, measurementEndPoint.y, 0).add(chordPerp.clone().multiplyScalar(extraOffset));
        
        // Update offsetPerp so endcaps are drawn correctly
        offsetPerp = new THREE.Vector2(chordPerp.x, chordPerp.y);
      }
    }

    // Measurement line parallel offset from the wall (Main measurement line)
    segmentPoints.push([measurementLineStartVec.x, measurementLineStartVec.y, 0]);
    segmentPoints.push([measurementLineEndVec.x, measurementLineEndVec.y, 0]);

    const stopLineOffset = offsetPerp.clone().multiplyScalar(stopLineLength);

    // Start cap line
    segmentPoints.push([
      measurementLineStartVec.x + stopLineOffset.x,
      measurementLineStartVec.y + stopLineOffset.y,
      0,
    ]);
    segmentPoints.push([
      measurementLineStartVec.x - stopLineOffset.x,
      measurementLineStartVec.y - stopLineOffset.y,
      0,
    ]);

    // End cap line
    segmentPoints.push([
      measurementLineEndVec.x + stopLineOffset.x,
      measurementLineEndVec.y + stopLineOffset.y,
      0,
    ]);
    segmentPoints.push([
      measurementLineEndVec.x - stopLineOffset.x,
      measurementLineEndVec.y - stopLineOffset.y,
      0,
    ]);

    let finalLength = Math.max(0, measurementEndT - measurementStartT);
    let finalHidden = Math.max(0, wallLength - finalLength);

    if (isWallType(object) && object.lineForm === "arc" && object.controlPoint) {
       finalLength = measurementStartPoint.distanceTo(measurementEndPoint);
       // hidden length is less meaningful for arcs, keep it 0 or approximate
       finalHidden = 0;
    }

    return {
      segmentPoints,
      measurementLineStart: measurementLineStartVec,
      measurementLineEnd: measurementLineEndVec,
      offsetPerpendicular: offsetPerp,
      chosenSide,
      measurementLength: finalLength,
      hiddenMeasurementLength: finalHidden,
    };
  }, [
    object.start.x,
    object.start.y,
    object.end.x,
    object.end.y,
    rulerLineOffset,
    stopLineLength,
    // Recompute when wall updates even if start/end stay the same
    object.lastUpdate,
    outerPerpendicular,
  ]);

  const displayedLength = isWallType(object)
    ? measurementLength
    : isRulerLineType(object)
      ? floorplannerStore.rulerLineLength(object.id)
      : isSingleLineType(object)
        ? floorplannerStore.singleLineLength(object.id)
        : 0;

  useEffect(() => {
    if (chosenSide && isWallType(object)) {
      floorplannerStore.setMeasurementSidePreference(object.id, chosenSide);
    }
  }, [chosenSide, object.id]);

  const measurementLineCenter = useMemo(
    () =>
      new THREE.Vector3(
        (measurementLineStart.x + measurementLineEnd.x) / 2,
        (measurementLineStart.y + measurementLineEnd.y) / 2,
        0
      ),
    [measurementLineStart, measurementLineEnd]
  );

  const outerTextPosition = useMemo(
    () =>
      new THREE.Vector3(
        measurementLineCenter.x +
        offsetPerpendicular.x * (rulerOuterTextOffset - rulerLineOffset),
        measurementLineCenter.y +
        offsetPerpendicular.y * (rulerOuterTextOffset - rulerLineOffset),
        0.02
      ),
    [measurementLineCenter, offsetPerpendicular, rulerOuterTextOffset, rulerLineOffset]
  );

  // Don't render anything if measurements should be hidden
  if (!shouldShowMeasurements) {
    return <group />;
  }

  const handleLengthClick = (wallId: string) => {
    editorStore.setWallEditingInnerLength(wallId);
    const currentLength = worldToDisplayLength(
      displayedLength,
      editorStore.displayLengthUnit,
    );

    setTempLength(currentLength.toString());
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
    object: WallType | SingleLineType | RulerLineType
  ) => {
    if (editorStore.wallEditingOuterLength === object.id || editorStore.wallEditingInnerLength === object.id || editorStore.singleLineEditingLength === object.id || editorStore.rulerLineEditingLength === object.id || editorStore.areaEditingLength === object.id) {
      if (event.key === "Enter") {
        const newLength = parseFloat(tempLength);
        if (!isNaN(newLength)) {
          if (isWallType(object)) {
            floorplannerStore.setWallLength(
              object.id,
              displayLengthToWorld(newLength, editorStore.displayLengthUnit) + hiddenMeasurementLength,
              editorStore.whichEndToUpdate === "start" ? WallConnectionStart : editorStore.whichEndToUpdate === "end" ? WallConnectionEnd : undefined
            );
            setTimeout(() => {
              floorplannerStore.objectNeedsUpdate(object.id);
              // Also update all connected walls
              object.centerConnections?.forEach((connection) => {
                floorplannerStore.objectNeedsUpdate(connection.id);
              });
            }, 50);
            editorStore.setWallEditingInnerLength(undefined);
          } else if (isSingleLineType(object)) {
            floorplannerStore.setSingleLineLength(
              object.id,
              displayLengthToWorld(newLength, editorStore.displayLengthUnit),
              undefined
            );
            editorStore.setSingleLineEditingLength(undefined);
          }
          else if (isRulerLineType(object)) {
            floorplannerStore.setRulerLineLength(
              object.id,
              displayLengthToWorld(newLength, editorStore.displayLengthUnit),
              "end"
            );
            editorStore.setRulerLineEditingLength(undefined);
          }
        }
      }
    }
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTempLength(event.target.value);
  };
  return (
    <group
      position={[0, 0, 0.001]}
      userData={{
        isWallLength: true,
      }}
      onPointerEnter={!disablePointerEvents ? () => {
        if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
      } : undefined}
      onPointerLeave={!disablePointerEvents ? () => {
        if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
      } : undefined}
    >
      {/* A Measurement line parallel offset from the wall */}
      <SimpleLineSegments segments={segments} color="black" lineWidth={0.1} renderOrder={998} depthTest={false} />

      {/* Group for outer ruler measure */}
      <group
        position={outerTextPosition}
        rotation={[0, 0, textRotation]}
      >
        {editorStore.wallEditingInnerLength !== object.id && (
          <mesh position={[0, 0, -0.01]} renderOrder={999}>
            <planeGeometry
              args={[rulerTextBoxWidth + rulerTextBoxPadding, rulerTextBoxHeight]}
            />
            <meshBasicMaterial
              color={object?.selected ? 0xe5eaff : renderStore.labelBackgroundColor}
              depthTest={false}
              depthWrite={false}
            />
          </mesh>
        )}
        <group
          position={[0, 0, -0.02]}
          onClick={!disablePointerEvents ? () => handleLengthClick(object.id) : undefined}
        >
          {editorStore.wallEditingInnerLength === object.id ? (
            <Html
              center
              style={{
                position: "absolute",
                top: "-3px",
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={tempLength}
                onKeyPress={(event) => handleKeyPress(event, object)}
                onChange={handleInputChange}
                autoFocus
                style={{
                  background: "white",
                  border: "none",
                  color: "black",
                  fontSize: "10px",
                  textAlign: "center",
                  outline: "none",
                  width: "60px",
                  height: "15px",
                }}
                onFocus={(event) => event.target.select()}
              />
            </Html>
          ) : (
            <SimpleText
              position={[0, 0, 0.01]}
              color="#222222"
              fontSize={rulerTextFontSize}
              renderOrder={1000}
              depthTest={false}
            >
              {`${formatLength(displayedLength, editorStore.displayLengthUnit)}`}
            </SimpleText>

          )}
        </group>
      </group>

      {/* Group for inner ruler measure */}
      {/* {isWallType(object) &&
        floorplannerStore.wallIsConnected(object as WallType, true) && (
          <group
            position={new THREE.Vector3(
              position.x + innerPerpendicular.x * rulerInnerTextOffset,
              position.y + innerPerpendicular.y * rulerInnerTextOffset,
              0.011
            )}
            rotation={[0, 0, textRotation]}
          >
            <mesh position={[0, 0, -0.01]}>
              <planeGeometry
                args={[
                  rulerTextBoxWidth + rulerTextBoxPadding,
                  rulerTextBoxHeight ,
                ]}
              />
              <meshBasicMaterial color={"darkblue"} />
            </mesh>
            <Text
              position={[0, 0, 0]}
              fontSize={rulerTextFontSize }
              color="white"
              onClick={!disablePointerEvents ? () => handleLengthClick(object.id, "inner") : undefined}
          >
            {editorStore.wallEditingInnerLength === object.id ? (
              <Html
                center
                style={{
                  position: "absolute",
                  top: "-3px",
                }}
              >
                <input
                ref={inputRef}
                  type="text"
                  value={tempLength}
                  onKeyPress={(event) => handleKeyPress(event, object, "inner")}
                  onChange={handleInputChange}
                  autoFocus
                  style={{
                    background: "white",
                    border: "none",
                    color: "black",
                    fontSize: "10px",
                    textAlign: "center",
                    outline: "none",
                    width: "60px",
                    height: "15px",
                  }}
                  onFocus={(event) => event.target.select()}
                />
              </Html>
            ) : (
              `${convertWorldToMillimeter(
                isWallType(object)
                  ? floorplannerStore.wallInnerLength(object as WallType)
                  : isSingleLineType(object) ? floorplannerStore.singleLineLength((object as SingleLineType).id) : 0
                ).toLocaleString()}`
              )}
            </Text>
          </group>
        )} */}
    </group>
  );
});

export const MeasurementLine = React.memo(MeasurementLineComponent, (prevProps, nextProps) => {
  return (
    prevProps.object.lastUpdate === nextProps.object.lastUpdate &&
    prevProps.object.start.x === nextProps.object.start.x &&
    prevProps.object.start.y === nextProps.object.start.y &&
    prevProps.object.end.x === nextProps.object.end.x &&
    prevProps.object.end.y === nextProps.object.end.y &&
    prevProps.object.controlPoint?.x === nextProps.object.controlPoint?.x &&
    prevProps.object.controlPoint?.y === nextProps.object.controlPoint?.y
  );
});
